<template>
  <AppShell>
    <div class="page-head">
      <div>
        <h1>供需论坛</h1>
        <p class="sub">讨论某一张卡，或一场招标。</p>
      </div>
      <button type="button" class="btn" @click="openComposer">发布帖子</button>
    </div>

    <div v-if="config && config.external_enabled" class="ext">
      <span>外部社区已配置</span>
      <button type="button" class="ghost" @click="enterExternal">打开外部社区</button>
    </div>

    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="tip" class="ok">{{ tip }}</p>

      <section v-if="!current" class="list-wrap">
        <div class="filters">
          <button
            v-for="b in [{ id: '', label: '全部' }, ...boards]"
            :key="b.id || 'all'"
            type="button"
            class="chip"
            :class="{ on: board === b.id }"
            @click="setBoard(b.id)"
          >{{ b.label }}</button>
        </div>
        <form class="search" @submit.prevent="searchThreads">
          <input v-model="keyword" placeholder="搜索标题或正文" />
          <button type="submit" class="ghost">搜索</button>
        </form>
        <p v-if="filterCardName || filterBidId" class="filter-tip">
          <span v-if="filterCardName">筛选卡：{{ filterCardName }}</span>
          <span v-if="filterBidId">筛选招标 #{{ filterBidId }}</span>
          <button type="button" class="ghost sm" @click="clearFilters">清除筛选</button>
        </p>
        <p v-if="loading" class="muted">加载中…</p>
        <p v-else-if="!threads.length" class="muted">
          没有帖子。
          <button type="button" class="linkish" @click="openComposer">去发布第一条</button>
        </p>
        <button
          v-for="item in threads"
          :key="item.id"
          type="button"
          class="thread"
          @click="openThread(item.id)"
        >
          <strong>{{ item.title }}</strong>
          <span class="meta">
            {{ boardLabel(item.board) }} · {{ item.author_email }} · {{ formatTime(item.created_at) }}
            · {{ item.reply_count }} 回复
            <em v-if="item.card_name"> · 卡 {{ item.card_name }}</em>
            <em v-if="item.bid_label"> · {{ item.bid_label }}</em>
            <em v-if="item.status !== 'published'"> · {{ statusLabel(item.status) }}</em>
          </span>
        </button>
        <ListPager
          :page="threadPaging.page"
          :pages="threadPaging.pages"
          :total="threadPaging.total"
          @change="loadThreads"
        />
      </section>

      <section v-else class="detail">
        <button type="button" class="ghost" @click="backList">返回列表</button>
        <article class="panel post">
          <h2>{{ current.thread.title }}</h2>
          <p class="meta">
            {{ boardLabel(current.thread.board) }} · {{ current.thread.author_email }}
            · {{ formatTime(current.thread.created_at) }}
            · {{ statusLabel(current.thread.status) }}
          </p>
          <div v-if="current.thread.card_name || current.thread.bid_label" class="refs">
            <router-link
              v-if="current.thread.card_id"
              class="ref"
              :to="{ path: '/', query: { q: current.thread.card_name } }"
            >算力卡：{{ current.thread.card_name }}</router-link>
            <router-link
              v-if="current.thread.bid_job_id"
              class="ref"
              :to="{ path: '/bid', query: { job: String(current.thread.bid_job_id) } }"
            >招标案例：{{ current.thread.bid_label }}</router-link>
          </div>
          <div class="rich-body" v-html="displayHtml(current.thread.body)" />
          <div class="actions">
            <button v-if="loggedIn" type="button" class="ghost" @click="startReply()">回复主题</button>
            <router-link v-else class="ghost linkish" to="/account?mode=login">登录后回复</router-link>
            <button v-if="current.thread.can_delete" type="button" class="ghost" @click="removeThread">删除主题</button>
            <template v-if="current.is_admin && current.thread.status !== 'published'">
              <button type="button" class="ghost" @click="reviewThread('published')">通过</button>
              <button type="button" class="ghost" @click="reviewThread('rejected')">驳回</button>
            </template>
            <template v-if="current.is_admin && current.thread.status === 'published'">
              <button type="button" class="ghost" @click="reviewThread('rejected')">下架</button>
            </template>
          </div>
        </article>

        <div class="reply-head">
          <h3>回复（{{ postPaging.total || current.posts.length }}）</h3>
        </div>
        <p v-if="!current.posts.length" class="muted">还没有回复，来抢沙发。</p>
        <article
          v-for="post in current.posts"
          :id="'post-' + post.id"
          :key="post.id"
          class="panel post reply"
        >
          <p class="meta">
            {{ post.author_email }} · {{ formatTime(post.created_at) }}
            <em v-if="post.status !== 'published'"> · {{ statusLabel(post.status) }}</em>
          </p>
          <div v-if="post.parent_author" class="quote">
            回复 {{ post.parent_author }}：{{ post.parent_excerpt || '…' }}
          </div>
          <div class="rich-body" v-html="displayHtml(post.body)" />
          <div class="actions">
            <button v-if="loggedIn" type="button" class="ghost sm" @click="startReply(post)">回复</button>
            <router-link v-else class="ghost sm linkish" to="/account?mode=login">登录后回复</router-link>
            <button v-if="post.can_delete" type="button" class="ghost sm" @click="removePost(post.id)">删除</button>
            <template v-if="current.is_admin && post.status !== 'published'">
              <button type="button" class="ghost sm" @click="reviewPost(post.id, 'published')">通过</button>
              <button type="button" class="ghost sm" @click="reviewPost(post.id, 'rejected')">驳回</button>
            </template>
          </div>
        </article>
        <ListPager
          :page="postPaging.page"
          :pages="postPaging.pages"
          :total="postPaging.total"
          @change="loadPostsPage"
        />

        <form v-if="loggedIn" id="reply-box" class="composer panel" @submit.prevent="submitReply">
          <h3>{{ replyTarget ? `回复 ${replyTarget.author_email}` : '回复主题' }}</h3>
          <p v-if="replyTarget" class="reply-target">
            <span>引用：{{ replyTarget.parent_excerpt || stripHtml(replyTarget.body).slice(0, 80) }}</span>
            <button type="button" class="ghost sm" @click="clearReplyTarget">取消引用</button>
          </p>
          <RichTextEditor ref="replyEditor" v-model="replyBody" placeholder="写下你的看法，支持加粗、列表、链接…" min-height="120px" />
          <div class="composer-actions">
            <button type="submit" :disabled="posting">{{ posting ? '发送中…' : '发布回复' }}</button>
          </div>
        </form>
        <div v-else class="composer panel">
          <p>登录邮箱账号后可以回复。</p>
          <router-link class="btn" to="/account?mode=login">去登录</router-link>
        </div>
      </section>

    <!-- 发帖弹窗 -->
    <div v-if="showComposer" class="modal" @click.self="closeComposer">
      <div class="modal-panel">
        <div class="modal-head">
          <h2>发布帖子</h2>
          <button type="button" class="ghost sm" @click="closeComposer">关闭</button>
        </div>
        <form @submit.prevent="submitThread">
          <select v-model="draftBoard">
            <option v-for="b in boards" :key="b.id" :value="b.id">{{ b.label }}</option>
          </select>
          <input v-model="draftTitle" maxlength="200" placeholder="标题，如：昇腾 910B 招标怎么报" />
          <RichTextEditor v-model="draftBody" placeholder="正文，支持加粗、标题、列表、链接、引用…" min-height="180px" />

          <div class="link-box">
            <label class="link-label">关联算力卡</label>
            <div class="link-row">
              <input v-model="cardQuery" placeholder="搜索卡名关联，如 H100" @input="onCardQuery" />
              <button v-if="draftCard" type="button" class="ghost sm" @click="clearCard">清除</button>
            </div>
            <p v-if="draftCard" class="picked">已选：{{ draftCard.name }}</p>
            <ul v-if="cardOptions.length" class="opts">
              <li v-for="c in cardOptions" :key="c.id">
                <button type="button" @click="pickCard(c)">{{ c.name }}</button>
              </li>
            </ul>
          </div>

          <div class="link-box">
            <label class="link-label">关联招标案例</label>
            <select v-model="draftBidId">
              <option value="">不关联</option>
              <option v-for="b in bidOptions" :key="b.id" :value="String(b.id)">
                #{{ b.id }} {{ b.label }}（{{ b.status }}）
              </option>
            </select>
          </div>

          <div class="composer-actions">
            <button type="button" class="ghost" @click="closeComposer">取消</button>
            <button type="submit" :disabled="posting">{{ posting ? '发布中…' : '发布' }}</button>
          </div>
        </form>
      </div>
    </div>
  </AppShell>
</template>

<script>
import AppShell from '../layouts/AppShell.vue'
import { notifyAuthChanged } from '../utils/authBus'
import { sessionToken } from '../utils/session'
import ListPager from '../components/ListPager.vue'
import RichTextEditor from '../components/RichTextEditor.vue'
import {
  createForumPost,
  createForumThread,
  deleteForumPost,
  deleteForumThread,
  forumConfig,
  forumLinkOptions,
  forumSso,
  getForumThread,
  listForumThreads,
  reviewForumPost,
  reviewForumThread
} from '../api/platformService'

const STATUS = { pending: '待审核', published: '已发布', rejected: '已下架' }

function stripHtml(html) {
  const el = document.createElement('div')
  el.innerHTML = html || ''
  return (el.textContent || el.innerText || '').trim()
}

export default {
  name: 'ForumWorkspace',
  components: { AppShell, ListPager, RichTextEditor },
  data() {
    return {
      config: null,
      boards: [
        { id: 'general', label: '综合' },
        { id: 'catalog', label: '选型讨论' },
        { id: 'bid', label: '招标经验' },
        { id: 'price', label: '价格情报' },
        { id: 'product', label: '产品吐槽' }
      ],
      board: '',
      keyword: '',
      threads: [],
      threadPaging: { page: 1, pages: 1, total: 0 },
      postPaging: { page: 1, pages: 1, total: 0 },
      current: null,
      showComposer: false,
      draftTitle: '',
      draftBody: '',
      draftBoard: 'general',
      draftCard: null,
      draftBidId: '',
      cardQuery: '',
      cardOptions: [],
      bidOptions: [],
      filterCardId: '',
      filterCardName: '',
      filterBidId: '',
      replyBody: '',
      replyTarget: null,
      error: '',
      tip: '',
      needLogin: false,
      loading: false,
      posting: false,
      cardTimer: null
    }
  },
  computed: {
    loggedIn() {
      return Boolean(sessionToken())
    }
  },
  async mounted() {
    try {
      this.config = await forumConfig()
      if (this.config.boards) this.boards = this.config.boards
    } catch (e) {
      this.config = { builtin: true }
    }
    await this.applyRouteQuery()
    await this.loadLinkOptions()
    await this.loadThreads()
  },
  watch: {
    '$route.query': {
      deep: true,
      handler() {
        this.applyRouteQuery()
      }
    }
  },
  beforeUnmount() {
    if (this.cardTimer) clearTimeout(this.cardTimer)
  },
  methods: {
    stripHtml,
    displayHtml(html) {
      const raw = html || ''
      if (!raw.includes('<')) {
        const esc = raw
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>')
        return `<p>${esc}</p>`
      }
      return raw
    },
    boardLabel(id) {
      const hit = this.boards.find((b) => b.id === id)
      return hit ? hit.label : id || '综合'
    },
    statusLabel(status) {
      return STATUS[status] || status
    },
    formatTime(iso) {
      if (!iso) return ''
      return iso.replace('T', ' ').slice(0, 16)
    },
    goLogin() {
      this.$router.push({ path: '/account', query: { mode: 'login' } })
    },
    openComposer() {
      if (!sessionToken()) {
        this.goLogin()
        return
      }
      this.showComposer = true
      this.error = ''
    },
    closeComposer() {
      this.showComposer = false
    },
    async applyRouteQuery() {
      const q = this.$route.query || {}
      if (q.card_id) {
        this.filterCardId = String(q.card_id)
        this.draftCard = {
          id: Number(q.card_id),
          name: q.card_name || q.title || `卡 #${q.card_id}`
        }
        this.filterCardName = this.draftCard.name
        this.cardQuery = this.draftCard.name
        this.draftBoard = 'catalog'
        try {
          const data = await forumLinkOptions({ card_id: q.card_id, q: this.draftCard.name })
          const hit = (data.cards || []).find((c) => String(c.id) === String(q.card_id))
          if (hit) {
            this.draftCard = hit
            this.filterCardName = hit.name
            this.cardQuery = hit.name
          }
          if (data.bids) this.bidOptions = data.bids
        } catch (e) {
          /* ignore */
        }
      }
      if (q.bid_job_id) {
        this.filterBidId = String(q.bid_job_id)
        this.draftBidId = String(q.bid_job_id)
        this.draftBoard = 'bid'
        const opt = {
          id: Number(q.bid_job_id),
          label: q.bid_label || q.title || `招标任务 #${q.bid_job_id}`,
          status: 'done'
        }
        if (!this.bidOptions.some((b) => String(b.id) === String(opt.id))) {
          this.bidOptions = [opt, ...this.bidOptions]
        }
      }
      if (q.title && !this.draftTitle) {
        this.draftTitle = String(q.title).slice(0, 200)
      }
      if (q.compose === '1' || q.card_id || q.bid_job_id) {
        if (!q.thread) {
          if (!sessionToken()) this.goLogin()
          else this.showComposer = true
        }
      }
      if (q.thread) {
        await this.openThread(Number(q.thread))
      }
    },
    async loadLinkOptions() {
      try {
        const data = await forumLinkOptions({ q: this.cardQuery || '' })
        this.bidOptions = data.bids || []
        if (this.cardQuery) this.cardOptions = data.cards || []
      } catch (e) {
        /* ignore */
      }
    },
    onCardQuery() {
      if (this.cardTimer) clearTimeout(this.cardTimer)
      this.cardTimer = setTimeout(async () => {
        const q = this.cardQuery.trim()
        if (q.length < 1) {
          this.cardOptions = []
          return
        }
        try {
          const data = await forumLinkOptions({ q })
          this.cardOptions = data.cards || []
          if (data.bids) this.bidOptions = data.bids
        } catch (e) {
          this.cardOptions = []
        }
      }, 280)
    },
    pickCard(c) {
      this.draftCard = c
      this.cardQuery = c.name
      this.cardOptions = []
      if (this.draftBoard === 'general') this.draftBoard = 'catalog'
    },
    clearCard() {
      this.draftCard = null
      this.cardQuery = ''
      this.cardOptions = []
    },
    clearFilters() {
      this.filterCardId = ''
      this.filterCardName = ''
      this.filterBidId = ''
      this.threadPaging.page = 1
      this.loadThreads()
    },
    setBoard(id) {
      this.board = id
      this.threadPaging.page = 1
      this.loadThreads()
    },
    searchThreads() {
      this.threadPaging.page = 1
      this.loadThreads()
    },
    async loadThreads(page) {
      this.error = ''
      this.needLogin = false
      this.loading = true
      if (page) this.threadPaging.page = page
      try {
        const params = { page: this.threadPaging.page, per_page: 20 }
        if (this.board) params.board = this.board
        if (this.keyword.trim()) params.q = this.keyword.trim()
        if (this.filterCardId) params.card_id = this.filterCardId
        if (this.filterBidId) params.bid_job_id = this.filterBidId
        const data = await listForumThreads(params)
        this.threads = data.threads || []
        this.threadPaging = data.paging || { page: 1, pages: 1, total: 0 }
        if (data.boards) this.boards = data.boards
      } catch (err) {
        this.threads = []
        this.needLogin = err.response?.status === 401
        if (!this.needLogin) this.error = err.response?.data?.error || '加载失败'
      } finally {
        this.loading = false
      }
    },
    async submitThread() {
      this.error = ''
      this.tip = ''
      if (stripHtml(this.draftBody).length < 8) {
        this.error = '正文至少 8 个字'
        return
      }
      this.posting = true
      try {
        const payload = {
          title: this.draftTitle,
          body: this.draftBody,
          board: this.draftBoard
        }
        if (this.draftCard?.id) payload.card_id = this.draftCard.id
        if (this.draftBidId) payload.bid_job_id = Number(this.draftBidId)
        const created = await createForumThread(payload)
        this.draftTitle = ''
        this.draftBody = ''
        this.clearCard()
        this.draftBidId = ''
        this.showComposer = false
        this.tip = created?.points_granted
          ? `已发布，获得 ${created.points_granted} 积分。`
          : created?.points_capped
            ? '已发布。今日发帖积分已达上限。'
            : '已发布。'
        if (created?.points_granted) notifyAuthChanged()
        await this.loadThreads()
        if (created?.id) await this.openThread(created.id)
      } catch (err) {
        this.error = err.response?.data?.error || '发帖失败'
        this.needLogin = err.response?.status === 401
      } finally {
        this.posting = false
      }
    },
    async openThread(id, page) {
      this.error = ''
      if (this.current?.thread?.id !== id) this.postPaging.page = 1
      if (page) this.postPaging.page = page
      try {
        this.current = await getForumThread(id, { page: this.postPaging.page, per_page: 50 })
        this.postPaging = this.current.posts_paging || { page: 1, pages: 1, total: 0 }
        if (!page) {
          this.replyBody = ''
          this.replyTarget = null
        }
      } catch (err) {
        if (err.response?.status === 401) {
          this.goLogin()
          return
        }
        this.error = err.response?.data?.error || '加载失败'
      }
    },
    loadPostsPage(page) {
      if (!this.current?.thread?.id) return
      this.openThread(this.current.thread.id, page)
    },
    backList() {
      this.current = null
      this.replyTarget = null
      this.loadThreads()
    },
    startReply(post = null) {
      this.replyTarget = post
        ? {
            id: post.id,
            author_email: post.author_email,
            body: post.body,
            parent_excerpt: stripHtml(post.body).slice(0, 80)
          }
        : null
      this.$nextTick(() => {
        const box = document.getElementById('reply-box')
        if (box) box.scrollIntoView({ behavior: 'smooth', block: 'center' })
        this.$refs.replyEditor?.focus()
      })
    },
    clearReplyTarget() {
      this.replyTarget = null
    },
    async submitReply() {
      if (!this.current?.thread?.id) return
      this.error = ''
      this.tip = ''
      if (stripHtml(this.replyBody).length < 2) {
        this.error = '回复至少 2 个字'
        return
      }
      this.posting = true
      try {
        const payload = { body: this.replyBody }
        if (this.replyTarget?.id) payload.parent_id = this.replyTarget.id
        await createForumPost(this.current.thread.id, payload)
        this.replyBody = ''
        this.$refs.replyEditor?.clear()
        this.replyTarget = null
        this.tip = '已回复。'
        await this.openThread(this.current.thread.id, 9999)
      } catch (err) {
        this.error = err.response?.data?.error || '回复失败'
        this.needLogin = err.response?.status === 401
      } finally {
        this.posting = false
      }
    },
    async removeThread() {
      if (!this.current?.thread?.id || !window.confirm('删除整帖及回复？')) return
      await deleteForumThread(this.current.thread.id)
      this.current = null
      await this.loadThreads()
    },
    async removePost(id) {
      if (!window.confirm('删除这条回复？')) return
      await deleteForumPost(id)
      await this.openThread(this.current.thread.id, this.postPaging.page)
    },
    async reviewThread(status) {
      await reviewForumThread(this.current.thread.id, status)
      await this.openThread(this.current.thread.id, this.postPaging.page)
    },
    async reviewPost(id, status) {
      await reviewForumPost(id, status)
      await this.openThread(this.current.thread.id, this.postPaging.page)
    },
    async enterExternal() {
      this.error = ''
      try {
        const data = await forumSso()
        window.open(data.url, '_blank', 'noopener')
      } catch (err) {
        this.error = err.response?.data?.error || '无法进入外部社区'
        this.needLogin = err.response?.status === 401
      }
    }
  }
}
</script>

<style scoped>
h1 { font-size: 34px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 8px; }
.page-head {
  display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 8px;
}
.sub { color: #86909c; margin-bottom: 12px; line-height: 1.6; font-size: 15px; }
.muted { color: #4e5969; margin-bottom: 12px; line-height: 1.6; }
.banner {
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  background: #e8f3ff; border: 1px solid #bedaff; border-radius: 16px; padding: 12px 14px; margin-bottom: 12px;
}
.ext {
  display: flex; gap: 10px; align-items: center; margin-bottom: 12px; color: #4e5969; font-size: 13px;
}
.list-wrap { max-width: 860px; }
.panel {
  background: #fff; border: 1px solid #e5e6eb; border-radius: 16px; padding: 14px; margin-bottom: 12px;
}
.composer select, .composer input, .modal-panel select, .modal-panel > form > input,
.modal-panel form > input, form > select, form > input {
  width: 100%; border: 1px solid #e5e6eb; border-radius: 12px; padding: 8px 10px; font: inherit; margin-bottom: 10px;
  box-sizing: border-box;
}
.link-box { margin: 12px 0; }
.link-label { display: block; font-size: 12px; color: #86909c; margin-bottom: 4px; }
.link-row { display: flex; gap: 6px; }
.link-row input { margin-bottom: 0; flex: 1; }
.picked { margin: 4px 0 0; font-size: 12px; color: #165dff; }
.opts { list-style: none; margin: 4px 0 0; padding: 0; max-height: 140px; overflow: auto; border: 1px solid #e5e6eb; border-radius: 12px; }
.opts button {
  width: 100%; text-align: left; background: #fff; color: #1d2129; border: 0; border-bottom: 1px solid #f2f3f5;
  padding: 8px 10px; border-radius: 0; font-size: 13px;
}
.opts button:hover { background: #e8f3ff; }
.refs { display: flex; flex-wrap: wrap; gap: 8px; margin: 8px 0; }
.ref {
  font-size: 12px; color: #165dff; background: #e8f3ff; border-radius: 12px; padding: 4px 8px; text-decoration: none;
}
.search { display: flex; gap: 8px; margin-bottom: 12px; }
.search input {
  flex: 1; margin: 0; border: 1px solid #e5e6eb; border-radius: 12px; padding: 8px 10px; font: inherit;
}
.filters { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.filter-tip { font-size: 12px; color: #4e5969; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.chip {
  border: 1px solid #e5e6eb; background: #fff; color: #4e5969; border-radius: 12px; padding: 4px 10px; cursor: pointer;
}
.chip.on { color: #165dff; border-color: #165dff; background: #e8f3ff; }
button, .btn {
  border: 0; background: #165dff; color: #fff; border-radius: 12px; padding: 8px 14px; cursor: pointer; text-decoration: none; display: inline-block;
}
.ghost { background: #fff; color: #1d2129; border: 1px solid #c9cdd4; margin-bottom: 8px; margin-right: 6px; }
.ghost.sm { padding: 4px 8px; font-size: 12px; margin-bottom: 0; }
.linkish {
  border: 0; background: transparent; color: #165dff; padding: 0; cursor: pointer; text-decoration: underline;
}
.thread {
  width: 100%; text-align: left; background: #fff; color: #1d2129; border: 1px solid #e5e6eb;
  border-radius: 16px; padding: 12px; margin-bottom: 8px; display: grid; gap: 4px;
}
.thread:hover { border-color: #165dff; }
.meta { color: #86909c; font-size: 12px; }
.rich-body {
  line-height: 1.7; margin: 10px 0 0; color: #1d2129; word-break: break-word;
}
.rich-body :deep(p) { margin: 0 0 0.6em; }
.rich-body :deep(h3) { margin: 0.8em 0 0.4em; font-size: 16px; }
.rich-body :deep(blockquote) {
  margin: 8px 0; padding: 6px 12px; border-left: 3px solid #bedaff; background: #f7f8fa; color: #4e5969;
}
.rich-body :deep(ul), .rich-body :deep(ol) { margin: 6px 0; padding-left: 22px; }
.rich-body :deep(a) { color: #165dff; }
.reply { background: #fafbfc; }
.reply-head h3 { margin: 8px 0 12px; font-size: 16px; }
.quote {
  font-size: 12px; color: #4e5969; background: #f2f3f5; border-left: 3px solid #c9cdd4;
  padding: 6px 10px; margin: 8px 0; border-radius: 0 4px 4px 0;
}
.reply-target {
  display: flex; justify-content: space-between; gap: 8px; align-items: center;
  font-size: 12px; color: #4e5969; background: #f7f8fa; padding: 8px 10px; border-radius: 12px; margin-bottom: 8px;
}
.actions { margin-top: 10px; }
.composer-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
.composer-actions .ghost { margin: 0; }
.error { color: #d3382c; margin: 8px 0; }
.ok { color: #188038; margin: 8px 0; }
.modal {
  position: fixed; inset: 0; background: rgba(0,0,0,.35); z-index: 90;
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.modal-panel {
  background: #fff; border-radius: 16px; width: min(720px, 100%); max-height: 90vh;
  overflow: auto; padding: 16px 18px;
}
.modal-head {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;
}
.modal-head h2 { margin: 0; font-size: 18px; }
@media (max-width: 800px) {
  .page-head { flex-direction: column; align-items: stretch; }
}
</style>
